import React, { Component } from 'react';
import queryString from 'query-string';
import InfiniteScroll from 'react-infinite-scroller';
import json2csv from 'json2csv';
import fileSaver from 'file-saver';
import jstz from 'jstimezonedetect';
import clsx from 'clsx';

import DataTable from '../Controls/DataTable/DataTable';
import * as filterTypes from '../Controls/Filters/filterTypes';
import { formatDate } from '@silverfort-nodelibs/common/utils/common';
import { EVENT_LEVEL, INFINITE_SCROLL_BATCH_SIZE } from '@silverfort-nodelibs/common/utils/consts';
import { bigNumberToText, formatEventDescription, httpPost, localTimezoneUtc } from '../utils';
import styles from './SystemNotifications.module.css';
import exportIcon from '../common/Images/export.png';
import refreshIcon from '../common/Images/refresh.png';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import FiltersMenu from '../Controls/Filters/FiltersMenu';
import { EVENTS_FILTERS } from '../Controls/Filters/FiltersObjects';
import { Tooltip } from '@material-ui/core';

const severityColorMapper = {
    [EVENT_LEVEL.DEBUG.value]: {
        description: 'DEBUG',
        background: '#000'
    },
    [EVENT_LEVEL.INFO.value]: {
        description: 'INFO',
        background: '#188ded'
    },
    [EVENT_LEVEL.WARNING.value]: {
        description: 'WARNING',
        background: '#fb9e44'
    },
    [EVENT_LEVEL.ERROR.value]: {
        description: 'ERROR',
        background: '#f12047'
    }
};

class Events extends Component {
    constructor(props) {
        super(props);
        this.columns = [
            {
                header: `Time ${localTimezoneUtc()}`,
                accessor: 'timestamp',
                columnRenderer: (date) => {
                    const timestamp = formatDate(date, navigator, false).split(' ');
                    return (
                        <div className={styles.timeColumn}>
                            <div className={styles.topRow}>{timestamp[1]}</div>
                            <div className={styles.bottomRow}>{timestamp[0]}</div>
                        </div>
                    );
                },
                width: 160
            },
            {
                header: 'Level',
                accessor: 'level',
                columnRenderer: (severity) => (
                    <div
                        className="tag"
                        style={{
                            background:
                                severityColorMapper[severity] && severityColorMapper[severity].background
                                    ? severityColorMapper[severity].background
                                    : ''
                        }}>
                        {severityColorMapper[severity] && severityColorMapper[severity].description
                            ? severityColorMapper[severity].description
                            : severity}
                    </div>
                ),
                width: 120
            },
            {
                header: 'Component',
                accessor: 'event_source',
                width: 180,
                columnRenderer: (event_source) => (
                    <Tooltip title={event_source}>
                        <div className="overflow-ellipsis overflow-hidden">{event_source}</div>
                    </Tooltip>
                )
            },
            {
                header: 'Code',
                accessor: 'code',
                width: 80
            },
            {
                header: 'Description',
                accessor: 'description',
                extra_data: 'domain_controller',
                width: 'auto',
                wrapText: true,
                columnRenderer: (description, dc) => <div>{formatEventDescription(description, dc)}</div>
            }
        ];

        if (this.shouldHideComponentColumn()) {
            this.columns = this.columns.filter((column) => column.header !== 'Component');
        }
    }

    shouldHideComponentColumn() {
        return this.props.systemMode === SYSTEM_MODES.SAAS || this.props.isCloudOnly;
    }

    state = {
        filters: this.initFilters(),
        events: [],
        eventsCount: 0,
        lastIdentity: null,
        hasMore: false,
        initialLoading: true,
        duringExport: false,
        loadingMore: false
    };

    initFilters() {
        const filters = EVENTS_FILTERS;

        if (this.shouldHideComponentColumn()) {
            delete filters.eventSource;
        }

        if (this.props.isCloudOnly) {
            delete filters.domainController;
        }

        return filters;
    }

    componentDidMount() {
        const filterValues = queryString.parse(this.props.location.search, {
            arrayFormat: 'comma',
            parseNumbers: true
        });

        if (filterValues && Object.keys(filterValues).length > 0) {
            const updatedFilters = { ...this.state.filters };

            const excludedFilters = [];
            if (filterValues.excludedFilters) {
                Array.isArray(filterValues.excludedFilters)
                    ? excludedFilters.push(...filterValues.excludedFilters)
                    : excludedFilters.push(filterValues.excludedFilters);
            }

            for (const key of Object.keys(filterValues)) {
                if (key === 'excludedFilters') continue;

                const filter = updatedFilters[key];
                let value = filterValues[key];
                if (!filter || !value) {
                    continue;
                }

                if (filter.filterType === filterTypes.TEXTS) {
                    if (!Array.isArray(value)) {
                        value = value.toString().split(',');
                    }
                } else if (
                    (filter.filterType === filterTypes.SELECT || filter.filterType === filterTypes.DATE_TIME) &&
                    !Array.isArray(value)
                ) {
                    value = [value];
                }

                updatedFilters[key] = {
                    ...updatedFilters[key],
                    value,
                    isExcluded: excludedFilters.includes(key)
                };
            }
            this.setState({ filters: updatedFilters }, () => this.loadMore(false, true));
        } else {
            this.loadMore(false, true);
        }
    }

    saveToUrl(filtersData) {
        if (filtersData && Object.keys(filtersData).length > 0) {
            const query = { excludedFilters: [] };
            for (const filterId in filtersData) {
                query[filterId] = filtersData[filterId].value;
                if (filtersData[filterId].isExcluded) query.excludedFilters.push(filterId);
            }

            const queryURL = queryString.stringify(query, { arrayFormat: 'comma' });
            window.history.pushState(null, 'Silverfort', `/systemEvents?${queryURL}`);
        } else {
            window.history.pushState(null, 'Silverfort', '/systemEvents');
        }
    }

    buildFiltersData(filters) {
        const filtersData = {};

        for (const filter of Object.values(filters)) {
            if (filter.value) {
                if (Array.isArray(filter.value) && !filter.value.length) {
                    continue;
                }

                filtersData[filter.id] = { value: filter.value, isExcluded: filter.isExcluded };
            }
        }

        return filtersData;
    }

    filterValueChangedHandler = (filterId, newValue, isExcluded = false) => {
        const newFilters = { ...this.state.filters };
        newFilters[filterId] = { ...newFilters[filterId] };
        newFilters[filterId].value = newValue !== null ? newValue : newFilters[filterId].defaultValue;
        newFilters[filterId].isExcluded = isExcluded;

        const filtersData = this.buildFiltersData(newFilters);
        this.saveToUrl(filtersData);

        this.setState(
            {
                filters: newFilters,
                events: [],
                eventsCount: 0,
                lastIdentity: null,
                hasMore: false,
                initialLoading: true
            },
            () => this.loadMore()
        );
    };

    refreshClickedHandler = () => {
        this.setState(
            {
                events: [],
                eventsCount: 0,
                lastIdentity: null,
                hasMore: false,
                initialLoading: true,
                duringExport: false
            },
            () => this.loadMore()
        );
    };

    loadMore = (forExport = false, loadOptionsFromServer = false) => {
        if (this.state.loadingMore) {
            return;
        }

        this.setState({ loadingMore: true, hasMore: false, duringExport: forExport }, () => {
            const filtersData = this.buildFiltersData(this.state.filters);
            const postData = {
                filters: filtersData,
                lastIdentity: this.state.lastIdentity,
                forExport: forExport,
                localTimeZone: encodeURIComponent(jstz.determine().name())
            };
            httpPost('/systemEvents/events', postData)
                .then((response) => {
                    if (response.data) {
                        const events = response.data.events
                            ? [...this.state.events, ...response.data.events]
                            : [...this.state.events];
                        const eventsCount = response.data.eventsCount ? response.data.eventsCount : 0;
                        if (forExport) {
                            const fields = this.columns.map((c) => {
                                return {
                                    label: c.header,
                                    value: c.accessor
                                };
                            });

                            const exportEvents = [];
                            events.forEach((event) => {
                                exportEvents.push({
                                    ...event,
                                    timestamp: formatDate(event.timestamp, navigator),
                                    level: severityColorMapper[event.level].description
                                });
                            });

                            try {
                                const csvResult = json2csv.parse(exportEvents, { fields: fields });

                                const blob = new Blob([csvResult], { type: 'text/plain;charset=utf-8' });
                                fileSaver.saveAs(blob, 'silverfort-events.csv');
                            } catch (err) {
                                console.log(err);
                            }
                            this.setState({ duringExport: false });
                            return;
                        }

                        if (loadOptionsFromServer && response.data.sourceOptions && !this.shouldHideComponentColumn()) {
                            this.setState({
                                filters: {
                                    ...this.state.filters,
                                    eventSource: {
                                        ...this.state.filters.eventSource,
                                        filterConfig: {
                                            ...this.state.filters.eventSource.filterConfig,
                                            options: response.data.sourceOptions
                                        }
                                    }
                                }
                            });
                        }
                        this.setState({
                            events: events,
                            eventsCount: eventsCount,
                            lastIdentity: response.data.lastIdentity,
                            hasMore: response.data.events.length === INFINITE_SCROLL_BATCH_SIZE,
                            initialLoading: false
                        });
                    }
                })
                .finally(() => this.setState({ loadingMore: false }));
        });
    };

    render() {
        return (
            <div className="events-section">
                <div className="events-header">
                    <div className="left-side">
                        <div className="content-header">EVENTS</div>
                        <span className="events-count">{`(${bigNumberToText(this.state.eventsCount)})`}</span>
                        <div className="refreshButton" onClick={this.refreshClickedHandler}>
                            <img src={refreshIcon} alt="refresh" />
                        </div>
                    </div>
                    <div className="right-side">
                        {!this.state.duringExport && (
                            <div
                                className={clsx('content-header export-events', this.props.isCloudOnly && 'mr-5')}
                                onClick={() => this.loadMore(true)}>
                                <img src={exportIcon} alt="" />
                                EXPORT
                            </div>
                        )}
                        {this.state.duringExport && <div className="export-events">Generating...</div>}
                    </div>
                </div>
                <div className="events-container">
                    <FiltersMenu
                        filters={Object.values(this.state.filters)}
                        onValueChange={this.filterValueChangedHandler}
                    />
                    <div className="events-table-container light-scrollbar">
                        <InfiniteScroll
                            loadMore={() => this.loadMore(false, false)}
                            hasMore={this.state.hasMore}
                            useWindow={false}
                            loader={<div key={0} className="content-loader"></div>}>
                            <DataTable
                                filters={this.state.filters}
                                columns={this.columns}
                                data={this.state.events}
                                loading={this.state.initialLoading}
                            />
                        </InfiniteScroll>
                    </div>
                </div>
            </div>
        );
    }
}

export default Events;
